import React, { useEffect, createContext, useRef, useState } from 'react';
import Pusher, { Channel } from 'pusher-js';
import config from 'config';
import { useUnmount, useLocalStorageState } from 'ahooks';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import * as PusherApi from '../Common/api/pusher';
import { SocketIoEventMsg, SocketIoEntityMsg } from '../Common/types';
import {
  publishSocketIoEventMsg,
  publishSocketIoEntityMsg,
} from '../socketio/topics';

const PusherContext = createContext<Channel | undefined>(undefined);
export { PusherContext };

type PusherAuthResponse = {
  channel_id: string;
  app_key: string;
};
if (!config.IN_PRODUCTION) {
  Pusher.logToConsole = true;
}
export const connect = (appKey: string) =>
  new Pusher(appKey, { cluster: 'eu' });

const PusherProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const pusher = useRef<Pusher | undefined>();
  const [channel, setChannel] = useState<Channel | undefined>();
  const [account] = useLocalStorageState<AccountPersistInfo>('account');

  useEffect(() => {
    const subscribeChannel = async () => {
      const data: PusherAuthResponse = await PusherApi.fetchPusherKey({
        userToken: account?.userToken as string,
      });
      pusher.current = connect(data?.app_key);
      if (pusher.current !== undefined) {
        setChannel(pusher.current.subscribe(data?.channel_id));
        listenToEvents();
        listenToEntity();
      }
    };
    subscribeChannel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listenToEvents = () => {
    pusher.current?.bind('event', (data: SocketIoEventMsg) => {
      if (data) {
        publishSocketIoEventMsg(data);
      }
    });
  };

  const listenToEntity = () => {
    pusher.current?.bind('entity', (data: SocketIoEntityMsg) => {
      if (data) {
        publishSocketIoEntityMsg(data);
      }
    });
  };

  useUnmount(() => {
    if (pusher.current !== undefined && channel !== undefined) {
      pusher.current.unsubscribe(channel.name);
    }
  });

  return (
    <PusherContext.Provider value={channel}>{children}</PusherContext.Provider>
  );
};

export default PusherProvider;
